import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * (D6) Top-level React error boundary.  Catches render-time exceptions in
 * the tree below it so a single bad component (e.g. malformed bundle data
 * in `StatusBar` after a server-side schema change) doesn't blank out the
 * whole app.  Renders a minimal fallback with reload affordance and
 * reports to the telemetry hook if installed.
 *
 * Scope: ONE boundary wrapping `<Viewer shell />` is enough for the MVP — we
 * don't yet split per-feature islands.  When islands matter (a flyout
 * crashing shouldn't disable the viewport), wrap each lazily-loaded
 * subtree separately.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (err: Error, reset: () => void) => ReactNode;
  label?: string;
  reload?: () => void;
}

interface ErrorBoundaryState {
  err: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { err: null };

  static getDerivedStateFromError(err: Error): ErrorBoundaryState {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    const label = this.props.label || 'root';
    try {
      const w = window as unknown as {
        __HOLZCOM_TELEMETRY?: (kind: string, payload: unknown) => void;
      };
      w.__HOLZCOM_TELEMETRY?.('react.error', {
        label,
        message: err.message,
        stack: (err.stack || '').slice(0, 4000),
        componentStack: (info.componentStack || '').slice(0, 2000),
      });
    } catch {
      // swallow — telemetry failures must not re-enter the boundary
    }
  }

  reset = () => this.setState({ err: null });
  recover = () => {
    if (this.state.err && isModuleLoadError(this.state.err)) {
      (this.props.reload ?? (() => window.location.reload()))();
      return;
    }
    this.reset();
  };

  render() {
    const { err } = this.state;
    if (!err) return this.props.children;
    if (this.props.fallback) return this.props.fallback(err, this.reset);
    return (
      <div
        role="alert"
        style={{
          padding: '24px',
          margin: '32px auto',
          maxWidth: '640px',
          background: '#1a1c20',
          color: '#e8e8e8',
          border: '1px solid #ff6b6b40',
          borderRadius: '8px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '13px',
          lineHeight: 1.5,
        }}
      >
        <div style={{ color: '#ff8a8a', fontWeight: 600, marginBottom: '8px' }}>
          Что-то пошло не так
        </div>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: '#0f1012',
            padding: '8px 10px',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#bbb',
            marginBottom: '12px',
          }}
        >
          {err.message}
        </pre>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={this.recover}
            style={{
              background: '#2a2d33',
              color: '#e8e8e8',
              border: '1px solid #3a3e45',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            {isModuleLoadError(err) ? 'Загрузить страницу заново' : 'Попробовать снова'}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: '#2a2d33',
              color: '#e8e8e8',
              border: '1px solid #3a3e45',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Перезагрузить страницу
          </button>
        </div>
      </div>
    );
  }
}

export function isModuleLoadError(error: Error): boolean {
  const text = `${error.name}: ${error.message}`;
  return error.name === 'ChunkLoadError'
    || /Failed to fetch dynamically imported module/i.test(text)
    || /Importing a module script failed/i.test(text)
    || /Loading (?:CSS )?chunk [^ ]+ failed/i.test(text);
}
